import * as React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import storage from '@/utils/storage';
import store from '@/stores';

// 初始化插件
dayjs.extend(utc);
dayjs.extend(timezone);

export function getTimeZone() {
  return storage.get('timeZone') || window.CONFIG?.TIME_ZONE || 'Asia/Shanghai';
}

/**
 * 将时间戳转换为指定时区的格式化日期字符串
 * @param timestamp 时间戳（毫秒）
 * @param timeZone 时区
 * @param format 输出格式，默认 'YYYY-MM-DD HH:mm:ss'
 * @returns 格式化后的日期字符串
 */
export const formatWithTimeZone = (
  timestamp: number,
  timeZone: string,
  format: string = 'YYYY-MM-DD HH:mm:ss',
): string => {
  if (!timestamp) return '';
  return dayjs(timestamp).tz(timeZone).format(format);
};

/**
 * 将指定时区的日期字符串转换为时间戳
 * @param dateString 日期字符串
 * @param timeZone 时区
 * @param format 输入格式，默认 'YYYY-MM-DD HH:mm:ss'
 * @returns 时间戳（毫秒）
 */
export const parseWithTimeZone = (
  dateString: string,
  timeZone: string,
  format: string = 'YYYY-MM-DD HH:mm:ss',
): number => {
  if (!dateString) return 0;
  return dayjs.tz(dateString, format, timeZone).valueOf();
};

/**
 * 提供日期格式化的Hook
 * @returns 包含format和parse方法的对象，自动响应时区变化
 */
export const useDateFormat = () => {
  return React.useMemo(() => {
    const currentTimeZone = getTimeZone();

    // 时间戳 -> 格式化字符串
    const format = (timestamp: number, formatStr: string = 'YYYY-MM-DD HH:mm:ss') => {
      return formatWithTimeZone(timestamp, currentTimeZone, formatStr);
    };

    // 格式化字符串 -> 时间戳
    const parse = (dateString: string, formatStr: string = 'YYYY-MM-DD HH:mm:ss') => {
      return parseWithTimeZone(dateString, currentTimeZone, formatStr);
    };

    console.log('当前时区:', currentTimeZone);
    return {
      format,
      parse,
      formatDate: (timestamp: number) => format(timestamp, 'YYYY-MM-DD'),
      formatTime: (timestamp: number) => format(timestamp, 'HH:mm:ss'),
      formatDateTime: (timestamp: number) => format(timestamp, 'YYYY-MM-DD HH:mm:ss'),
      parseDate: (dateString: string) => parse(dateString, 'YYYY-MM-DD'),
      parseDateTime: (dateString: string) => parse(dateString, 'YYYY-MM-DD HH:mm:ss'),
    };
  }, [store.timeZone]);
};
